import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Home, Search, MapPin, Stethoscope, Shield,
  BookOpen, Phone, Building2, FileText, Scale
} from 'lucide-react';
import { mockStates } from '../data/mock';
import { facilityApi } from '../utils/api';

const SitemapPage = () => {
  const [siteStats, setSiteStats] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await facilityApi.getStats();
        setSiteStats(response);
      } catch (err) {
        console.error('Error fetching site stats:', err);
      }
    };

    fetchStats();
  }, []);
  const mainPages = [
    { name: 'Home', href: '/', icon: <Home className="h-4 w-4" />, description: 'Main landing page with search and featured facilities' },
    { name: 'Find Treatment', href: '/search', icon: <Search className="h-4 w-4" />, description: 'Search and filter treatment centers by location and services' },
    { name: 'Treatment Types', href: '/treatment-types', icon: <Stethoscope className="h-4 w-4" />, description: 'Learn about different levels of addiction treatment' },
    { name: 'Insurance Help', href: '/insurance', icon: <Shield className="h-4 w-4" />, description: 'Information about insurance coverage for treatment' },
    { name: 'Resources', href: '/resources', icon: <BookOpen className="h-4 w-4" />, description: 'Support groups, educational materials, and crisis resources' },
    { name: 'Get Help', href: '/get-help', icon: <Phone className="h-4 w-4" />, description: 'Contact form for personalized treatment assistance' }
  ];

  const facilityPages = [
    { name: 'List Your Facility', href: '/facility-signup', icon: <Building2 className="h-4 w-4" />, description: 'Register your treatment facility in our directory' },
    { name: 'Facility Login', href: '/facility-login', icon: <Building2 className="h-4 w-4" />, description: 'Login portal for registered treatment facilities' }
  ];

  const legalPages = [
    { name: 'About Us', href: '/about', icon: <FileText className="h-4 w-4" />, description: 'Learn about our mission and how our directory service works' },
    { name: 'Contact', href: '/contact', icon: <Phone className="h-4 w-4" />, description: 'Get in touch with our team for support and inquiries' },
    { name: 'Terms of Service', href: '/terms', icon: <Scale className="h-4 w-4" />, description: 'Legal terms and conditions for using our service' },
    { name: 'Privacy Policy', href: '/privacy', icon: <Shield className="h-4 w-4" />, description: 'How we collect, use, and protect your personal information' }
  ];

  // Group states by first letter for better organization; facility counts
  // come from the live database, not mockStates' placeholder numbers.
  const statesWithRealCounts = mockStates.map((state) => ({
    ...state,
    facilities: siteStats?.facilities_by_state?.[state.code]?.count ?? null,
  }));

  const groupedStates = statesWithRealCounts.reduce((acc, state) => {
    const firstLetter = state.name[0];
    if (!acc[firstLetter]) {
      acc[firstLetter] = [];
    }
    acc[firstLetter].push(state);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              Sitemap
            </h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Navigate through all pages and resources available on Addiction Rehab Call. 
              Find treatment centers, learn about addiction recovery, and access support resources.
            </p>
          </div>

          {/* Main Navigation Pages */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center">
                <Home className="h-5 w-5 mr-2" />
                Main Pages
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {mainPages.map((page) => (
                  <Link 
                    key={page.href}
                    to={page.href}
                    className="flex items-start p-3 rounded-lg border hover:bg-gray-50 transition-colors"
                  >
                    <div className="text-blue-600 mr-3 mt-0.5">
                      {page.icon}
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">{page.name}</h3>
                      <p className="text-sm text-gray-600">{page.description}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* State Pages */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center">
                <MapPin className="h-5 w-5 mr-2" />
                Treatment Centers by State ({mockStates.length} States)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="mb-4">
                <Link 
                  to="/states"
                  className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium"
                >
                  <MapPin className="h-4 w-4 mr-1" />
                  View All States Directory
                </Link>
              </div>
              
              {Object.keys(groupedStates).sort().map((letter) => (
                <div key={letter} className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 border-b pb-1">
                    {letter}
                  </h3>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
                    {groupedStates[letter].map((state) => (
                      <Link
                        key={state.code}
                        to={`/states/${state.code.toLowerCase()}`}
                        className="flex items-center justify-between p-2 rounded border hover:bg-gray-50 transition-colors text-sm"
                      >
                        <span>{state.name}</span>
                        <span className="text-gray-500">{state.facilities !== null ? `(${state.facilities})` : ''}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Facility Pages */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center">
                <Building2 className="h-5 w-5 mr-2" />
                For Treatment Facilities
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                {facilityPages.map((page) => (
                  <Link 
                    key={page.href}
                    to={page.href}
                    className="flex items-start p-3 rounded-lg border hover:bg-gray-50 transition-colors"
                  >
                    <div className="text-purple-600 mr-3 mt-0.5">
                      {page.icon}
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">{page.name}</h3>
                      <p className="text-sm text-gray-600">{page.description}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Legal & Info Pages */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center">
                <FileText className="h-5 w-5 mr-2" />
                Legal & Information Pages
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                {legalPages.map((page) => (
                  <Link 
                    key={page.href}
                    to={page.href}
                    className="flex items-start p-3 rounded-lg border hover:bg-gray-50 transition-colors"
                  >
                    <div className="text-gray-600 mr-3 mt-0.5">
                      {page.icon}
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">{page.name}</h3>
                      <p className="text-sm text-gray-600">{page.description}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Emergency Resources */}
          <Card className="border-red-200 bg-red-50">
            <CardHeader>
              <CardTitle className="flex items-center text-red-800">
                <Phone className="h-5 w-5 mr-2" />
                Emergency Resources
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="text-center p-4 bg-white rounded-lg border">
                  <h3 className="font-semibold text-red-800 mb-2">Crisis Support</h3>
                  <a href="tel:988" className="text-2xl font-bold text-red-600 hover:underline">
                    988
                  </a>
                  <p className="text-sm text-red-700 mt-1">Suicide & Crisis Lifeline</p>
                </div>
                
                <div className="text-center p-4 bg-white rounded-lg border">
                  <h3 className="font-semibold text-red-800 mb-2">Treatment Help</h3>
                  <a href="tel:1-800-662-4357" className="text-lg font-bold text-red-600 hover:underline">
                    1-800-662-HELP
                  </a>
                  <p className="text-sm text-red-700 mt-1">SAMHSA National Helpline</p>
                </div>
                
                <div className="text-center p-4 bg-white rounded-lg border">
                  <h3 className="font-semibold text-red-800 mb-2">Text Support</h3>
                  <span className="text-lg font-bold text-red-600">
                    HOME to 741741
                  </span>
                  <p className="text-sm text-red-700 mt-1">Crisis Text Line</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* XML Sitemap Note */}
          <div className="text-center mt-8 p-6 bg-gray-100 rounded-lg">
            <h3 className="font-semibold text-gray-900 mb-2">
              XML Sitemap for Search Engines
            </h3>
            <p className="text-gray-600 text-sm mb-4">
              Looking for our XML sitemap for SEO purposes?
            </p>
            <a 
              href="/sitemap.xml" 
              className="text-blue-600 hover:underline font-medium"
            >
              View XML Sitemap
            </a>
          </div>

          {/* Back to Home */}
          <div className="text-center mt-8">
            <Link 
              to="/" 
              className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium"
            >
              ← Back to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SitemapPage;